import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { KEEP } from '../../lib/usage.ts';

export const BOX = 'rounded-box border border-line bg-surface';

/** A page's rail beside the column, from `xl`; a page without one draws the inbox's. */
export const RAIL =
  'sticky top-[var(--app-head)] hidden h-[calc(100dvh-var(--app-head))] w-80 shrink-0 flex-col border-l border-line-soft pl-4 xl:flex';

export const EYEBROW = 'text-2xs font-semibold tracking-wider text-ink-faint uppercase';

export const TITLE = 'text-xl font-semibold tracking-tight text-ink';

export const CARD = 'text-sm font-semibold text-ink';

export const DAY = 'tnum text-2xs font-medium text-ink-faint';

export const GROUP = 'text-2xs font-semibold text-ink-muted';

export const SECTION = 'text-base font-semibold tracking-tight text-ink';

export const SUBJECT =
  'truncate text-xl leading-tight font-semibold tracking-tight text-ink sm:text-3xl';

/** `clip`, not `hidden`: `hidden` makes a scroll container, and a sticky head inside would never stick. */
export function Sheet({ children }: { children: ReactNode }) {
  return <section className={clsx(BOX, 'overflow-clip')}>{children}</section>;
}

export function Card({
  id,
  tone,
  className,
  children,
}: {
  id?: string;
  tone?: 'warn';
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      className={clsx(
        'gap-card p-card flex flex-col rounded-box border',
        tone === 'warn' ? 'border-warn/40 bg-warn/5' : 'border-line bg-surface',
        className,
      )}
    >
      {children}
    </section>
  );
}

export function List({
  as: Tag = 'ul',
  className,
  children,
}: {
  as?: 'ul' | 'ol' | 'div';
  className?: string;
  children: ReactNode;
}) {
  return <Tag className={clsx(BOX, 'divide-y divide-line-soft', className)}>{children}</Tag>;
}

export function Well({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={clsx(
        'flex flex-col gap-2.5 rounded-control border border-line-soft bg-canvas/40 p-2.5',
        className,
      )}
    >
      {children}
    </div>
  );
}

const NOTICE_TONE = {
  warn: 'bg-warn/10 text-warn',
  quiet: 'bg-surface-raised text-ink-muted',
} as const;

export function Notice({
  tone = 'warn',
  className,
  children,
}: {
  tone?: keyof typeof NOTICE_TONE;
  className?: string;
  children: ReactNode;
}) {
  return (
    <p
      className={clsx(
        'rounded-control px-2.5 py-1.5 text-xs leading-relaxed',
        NOTICE_TONE[tone],
        className,
      )}
    >
      {children}
    </p>
  );
}

const CHEVRON = { sm: 'size-3', md: 'size-3.5', lg: 'size-4' } as const;

export function Chevron({
  open,
  size = 'md',
  className,
}: {
  open: boolean;
  size?: keyof typeof CHEVRON;
  className?: string;
}) {
  return (
    <ChevronDown
      aria-hidden
      className={clsx(
        CHEVRON[size],
        'shrink-0 text-ink-faint transition-transform group-hover/disclose:text-ink',
        open && 'rotate-180',
        className,
      )}
    />
  );
}

interface DiscloseLook {
  chevron?: 'start' | 'end';
  chevronSize?: keyof typeof CHEVRON;
  chevronClassName?: string;
  title?: string;
  className?: string;
}

/** The button that shows or hides something, its chevron turned to say which. */
export function DiscloseButton({
  open,
  onToggle,
  chevron = 'end',
  chevronSize,
  chevronClassName,
  title,
  className,
  labelledBy,
  children,
}: DiscloseLook & {
  open: boolean;
  onToggle: () => void;
  labelledBy?: string;
  children?: ReactNode;
}) {
  const mark = <Chevron open={open} size={chevronSize} className={chevronClassName} />;
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-labelledby={labelledBy}
      title={title}
      onClick={onToggle}
      className={clsx('group/disclose', className)}
    >
      {chevron === 'start' && mark}
      {children}
      {chevron === 'end' && mark}
    </button>
  );
}

/** A `DiscloseButton` holding its own state, with the body it opens beneath it. */
export function Disclose({
  summary,
  defaultOpen = false,
  bodyClassName,
  children,
  ...look
}: DiscloseLook & {
  summary: ReactNode;
  defaultOpen?: boolean;
  bodyClassName?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <DiscloseButton {...look} open={open} onToggle={() => setOpen((current) => !current)}>
        {summary}
      </DiscloseButton>
      {open && <div className={clsx('ticket-in', bodyClassName)}>{children}</div>}
    </>
  );
}

export function Fold({
  title,
  blurb,
  defaultOpen = false,
  children,
}: {
  title: string;
  blurb: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    // Clipped so the hover fill follows the rounded corners whether open or shut.
    <section className={clsx(BOX, 'overflow-clip')}>
      <Disclose
        defaultOpen={defaultOpen}
        summary={
          <span className="flex min-w-0 flex-col">
            <span className="text-sm font-medium text-ink">{title}</span>
            <span className="text-xs text-ink-muted">{blurb}</span>
          </span>
        }
        className="py-panel flex w-full items-center justify-between gap-3 px-gutter text-left transition-colors hover:bg-surface-raised"
        chevronSize="lg"
        bodyClassName="p-card flex flex-col gap-3 border-t border-line-soft"
      >
        {children}
      </Disclose>
    </section>
  );
}

export function Hint({
  as: Tag = 'p',
  faint = false,
  className,
  children,
}: {
  as?: 'p' | 'span' | 'dd';
  faint?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Tag
      className={clsx(
        'text-xs leading-relaxed',
        faint ? 'text-ink-faint' : 'text-ink-muted',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export function Eyebrow({
  as: Tag = 'h3',
  className,
  children,
}: {
  as?: 'h2' | 'h3' | 'h4' | 'h5' | 'p' | 'span' | 'dt';
  className?: string;
  children: ReactNode;
}) {
  return <Tag className={clsx(KEEP, EYEBROW, className)}>{children}</Tag>;
}

export function StatStrip({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">{children}</div>;
}

export function Fact({
  label,
  beside = false,
  className,
  children,
}: {
  label: string;
  beside?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={beside ? 'contents' : 'flex min-w-0 flex-col gap-1'}>
      {beside ? <dt className="text-ink-faint">{label}</dt> : <Eyebrow as="dt">{label}</Eyebrow>}
      <dd className={clsx('min-w-0 text-ink', !beside && 'text-xs', className)}>{children}</dd>
    </div>
  );
}

export function Stat({
  label,
  title,
  warn = false,
  note,
  inList = false,
  children,
}: {
  label: string;
  title?: string;
  warn?: boolean;
  note?: ReactNode;
  inList?: boolean;
  children: ReactNode;
}) {
  const figure = (
    <span
      className={clsx(
        'tnum font-mono text-base leading-none font-semibold',
        warn ? 'text-warn' : 'text-ink',
      )}
    >
      {children}
    </span>
  );
  const Holder = inList ? 'dd' : 'span';
  return (
    <div className="flex flex-col items-start gap-1" title={title}>
      <Eyebrow as={inList ? 'dt' : 'span'}>{label}</Eyebrow>
      {note === undefined && !inList ? (
        figure
      ) : (
        <Holder className="flex flex-col gap-0.5">
          {figure}
          {note && <span className="text-xs text-ink-faint">{note}</span>}
        </Holder>
      )}
    </div>
  );
}
